import React, { useEffect, useState } from 'react';
import { formatDate, formatBillingDay } from './formatters';
import ContractDetails from './ContractDetails';
import DirectDebitDetails from './DirectDebitDetails';

const extractBillingEmails = (contact) => {
    if (!contact) {
        return { primary: '', extras: [], all: [] };
    }

    let list = [];

    if (Array.isArray(contact.billingEmails) && contact.billingEmails.length) {
        list = contact.billingEmails.filter(Boolean);
    } else if (typeof contact.billingEmail === 'string') {
        list = contact.billingEmail
            .split('|')
            .map((x) => x.trim())
            .filter(Boolean);
    }

    const [primary = '', ...extras] = list;

    return { primary, extras, all: list };
};

const buildFormFromDetails = (details) => {
    const { overview, contact } = details || {};
    const { primary, extras } = extractBillingEmails(contact);

    return {
        contactName: overview?.contactName || '',
        address1: overview?.billingAddress?.address1 || '',
        address2: overview?.billingAddress?.address2 || '',
        address3: overview?.billingAddress?.address3 || '',
        townOrCity: overview?.billingAddress?.townOrCity || '',
        county: overview?.billingAddress?.county || '',
        postcode: overview?.billingAddress?.postcode || '',
        contactEmail: contact?.contactEmail || '',
        billingEmail: primary,
        billingEmails: extras,
        contactPhone: contact?.contactPhone || '',
        mobilePhone: contact?.mobilePhone || '',
        otherMobilePhone: contact?.otherMobilePhone || '',
    };
};

export default function YourDetails({
    details,
    canEditDetails = false,
    onSave,
    subtitle,
    directDebit
}) {
    const [form, setForm] = useState(
        () => (details ? buildFormFromDetails(details) : null)
    );
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        if (details) {
            setForm(buildFormFromDetails(details));
        }
    }, [details]);

    const isEditable = canEditDetails && typeof onSave === 'function';

    const handleFieldChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleBillingExtraChange = (index) => (e) => {
        const value = e.target.value;
        setForm((prev) => {
            const copy = [...(prev.billingEmails || [])];
            copy[index] = value;
            return { ...prev, billingEmails: copy };
        });
    };

    const handleAddBillingEmail = () => {
        setForm((prev) => ({
            ...prev,
            billingEmails: [...(prev.billingEmails || []), ''],
        }));
    };

    const handleRemoveBillingEmail = (index) => () => {
        setForm((prev) => {
            const copy = [...(prev.billingEmails || [])];
            copy.splice(index, 1);
            return { ...prev, billingEmails: copy };
        });
    };

    const handleEditClick = () => {
        if (!details || !form) return;
        if (!isEditable) return;

        setSaveError(null);
        setIsEditing(true);
    };

    const handleCancel = () => {
        if (details) {
            setForm(buildFormFromDetails(details));
        }
        setSaveError(null);
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!details || !form || !onSave) return;

        setIsSaving(true);
        setSaveError(null);

        try {
            const cleanExtras = (form.billingEmails || [])
                .map((e) => e.trim())
                .filter(Boolean);

            const allBillingEmails = [
                form.billingEmail?.trim(),
                ...cleanExtras,
            ].filter(Boolean);

            const joinedBillingEmails = allBillingEmails.join('|');

            const payload = {
                contactEmail: form.contactEmail,
                contactName: form.contactName,
                contactNumber: form.contactPhone,
                mobileNumber: form.mobilePhone,
                otherMobileNumber: form.otherMobilePhone,
                billingEmail: joinedBillingEmails,
                billingEmails: allBillingEmails,
                billingAddressLine1: form.address1,
                billingAddressLine2: form.address2,
                billingAddressLine3: form.address3,
                billingTownOrCity: form.townOrCity,
                billingCounty: form.county,
                billingPostcode: form.postcode,
            };

            await onSave(payload);
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to save customer details', err);
            setSaveError('Unable to save your details. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!details || !form) {
        return (
            <section className="your-details">
                <p>Unable to load your details.</p>
            </section>
        );
    }

    const { overview, contact, accountBalance, payment, contract } = details;
    const { primary: primaryBillingEmail, extras: extraBillingEmails } = extractBillingEmails(contact);

    const rawContractEnd =
        payment?.contractEnd ||
        contract?.terminationDate ||
        null;

    const rawBillingDay =
        accountBalance?.expectedBilling ??
        contract?.expectedBillingDay ??
        null;

    const resolvedPaymentMethod =
        accountBalance?.paymentMethod ||
        contract?.paymentMethod ||
        '—';

    const resolvedBillingTerm =
        accountBalance?.billingTerms ||
        contract?.billingTerms ||
        '—';

    const contractItems =
        rawContractEnd || rawBillingDay || resolvedPaymentMethod || resolvedBillingTerm
            ? [{
                contractEndDate: rawContractEnd ? formatDate(rawContractEnd) : '—',
                billingDate: rawBillingDay ? formatBillingDay(rawBillingDay) : '—',
                paymentMethod: resolvedPaymentMethod,
                billingTerm: resolvedBillingTerm,
            }]
            : [];

    return (
        <section className="your-details">
            <header className="your-details__header">
                <h2>Your details</h2>
                <p className="your-details__subtitle">
                    {subtitle || 'These details apply to your account only.'}
                </p>
            </header>

            <div className="generic-details__block your-details__card">
                <div className="your-details__grid">
                    {/* Account (read-only) */}
                    <div className="your-details__col">
                        <p className="details-label">Account</p>
                        <strong>{overview?.accountNumber ?? '—'}</strong>
                    </div>

                    {/* Company name (read-only) */}
                    <div className="your-details__col">
                        <p className="details-label">Company name</p>
                        <strong>{overview?.companyName ?? '—'}</strong>
                    </div>

                    {/* Contact name */}
                    <div className="your-details__col">
                        <p className="details-label">Contact name</p>
                        {isEditing ? (
                            <input
                                type="text"
                                value={form.contactName}
                                onChange={handleFieldChange('contactName')}
                            />
                        ) : (
                            <strong>{overview?.contactName ?? '—'}</strong>
                        )}
                    </div>

                    {/* Billing address */}
                    <div className="your-details__col your-details__col--address">
                        <p className="details-label">Billing address</p>
                        {isEditing ? (
                            <>
                                <input
                                    type="text"
                                    value={form.address1}
                                    onChange={handleFieldChange('address1')}
                                />
                                <input
                                    type="text"
                                    value={form.address2}
                                    onChange={handleFieldChange('address2')}
                                />
                                <input
                                    type="text"
                                    value={form.address3}
                                    onChange={handleFieldChange('address3')}
                                />
                                <input
                                    type="text"
                                    value={form.townOrCity}
                                    onChange={handleFieldChange('townOrCity')}
                                />
                            </>
                        ) : (
                            <strong>
                                {overview?.billingAddress?.address1 && <>{overview.billingAddress.address1}<br /></>}
                                {overview?.billingAddress?.address2 && <>{overview.billingAddress.address2}<br /></>}
                                {overview?.billingAddress?.address3 && <>{overview.billingAddress.address3}<br /></>}
                                {overview?.billingAddress?.townOrCity && <>{overview.billingAddress.townOrCity}<br /></>}
                            </strong>
                        )}
                    </div>

                    {/* Contact email */}
                    <div className="your-details__col">
                        <p className="details-label">Contact email</p>
                        {isEditing ? (
                            <input
                                type="email"
                                value={form.contactEmail}
                                onChange={handleFieldChange('contactEmail')}
                            />
                        ) : (
                            <strong>{contact?.contactEmail ?? '—'}</strong>
                        )}
                    </div>

                    {/* Billing email (+ additional emails) */}
                    <div className="your-details__col your-details__col--billing">
                        <p className="details-label">Billing email</p>

                        {isEditing ? (
                            <>
                                <input
                                    type="email"
                                    value={form.billingEmail}
                                    onChange={handleFieldChange('billingEmail')}
                                />

                                {(form.billingEmails || []).map((email, idx) => (
                                    <div
                                        key={idx}
                                        className="your-details__billing-extra"
                                    >
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={handleBillingExtraChange(idx)}
                                        />
                                        <button
                                            type="button"
                                            className="your-details__billing-remove"
                                            onClick={handleRemoveBillingEmail(idx)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    className="your-details__add-email"
                                    onClick={handleAddBillingEmail}
                                >
                                    + Add additional email
                                </button>
                            </>
                        ) : (
                            <>
                                <strong>{primaryBillingEmail || '—'}</strong>
                                {extraBillingEmails.length > 0 && (
                                    <ul className="your-details__billing-list">
                                        {extraBillingEmails.map((email, idx) => (
                                            <li key={idx}>{email}</li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>

                    {/* Contact number */}
                    <div className="your-details__col">
                        <p className="details-label">Contact number</p>
                        {isEditing ? (
                            <input
                                type="tel"
                                value={form.contactPhone}
                                onChange={handleFieldChange('contactPhone')}
                            />
                        ) : (
                            <strong>{contact?.contactPhone ?? '—'}</strong>
                        )}
                    </div>

                    {/* Town */}
                    <div className="your-details__col">
                        <p className="details-label">Town</p>
                        {isEditing ? (
                            <input
                                type="text"
                                value={form.townOrCity}
                                onChange={handleFieldChange('townOrCity')}
                            />
                        ) : (
                            <strong>{overview?.billingAddress?.townOrCity ?? '—'}</strong>
                        )}
                    </div>

                    {/* Mobile */}
                    <div className="your-details__col">
                        <p className="details-label">Mobile number</p>
                        {isEditing ? (
                            <input
                                type="tel"
                                value={form.mobilePhone}
                                onChange={handleFieldChange('mobilePhone')}
                            />
                        ) : (
                            <strong>{contact?.mobilePhone ?? '—'}</strong>
                        )}
                    </div>

                    {/* Other mobile */}
                    <div className="your-details__col">
                        <p className="details-label">Other mobile</p>
                        {isEditing ? (
                            <input
                                type="tel"
                                value={form.otherMobilePhone}
                                onChange={handleFieldChange('otherMobilePhone')}
                            />
                        ) : (
                            <strong>{contact?.otherMobilePhone ?? '—'}</strong>
                        )}
                    </div>

                    {/* County */}
                    <div className="your-details__col">
                        <p className="details-label">County</p>
                        {isEditing ? (
                            <input
                                type="text"
                                value={form.county}
                                onChange={handleFieldChange('county')}
                            />
                        ) : (
                            <strong>{overview?.billingAddress?.county ?? '—'}</strong>
                        )}
                    </div>

                    {/* Postcode */}
                    <div className="your-details__col">
                        <p className="details-label">Postcode</p>
                        {isEditing ? (
                            <input
                                type="text"
                                value={form.postcode}
                                onChange={handleFieldChange('postcode')}
                            />
                        ) : (
                            <strong>{overview?.billingAddress?.postcode ?? '—'}</strong>
                        )}
                    </div>

                    {isEditable && (
                        <div className="your-details__actions-row">
                            {isEditing ? (
                                <>
                                    <button
                                        type="button"
                                        className="your-details__btn your-details__btn--secondary"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="your-details__btn your-details__btn--primary"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving…' : 'Save'}
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="your-details__btn your-details__btn--secondary"
                                    onClick={handleEditClick}
                                >
                                    Edit details
                                </button>
                            )}
                        </div>
                    )}

                    {saveError && (
                        <p className="your-details__save-error">
                            {saveError}
                        </p>
                    )}
                </div>
            </div>

            {contractItems.length > 0 && (
                <ContractDetails contracts={contractItems} />
            )}

            {directDebit && (
                <DirectDebitDetails customerAccountNumber={overview.accountNumber} billingEmail={contact.billingEmail} />
            )}
        </section>
    );
}